'use client';
import React, { useEffect, useState } from 'react';

const VERIFICATION_STAGES = [
  { id: 'stage-format', label: 'Validating transaction hash format', duration: 800 },
  { id: 'stage-broadcast', label: 'Querying Bitcoin blockchain', duration: 1500 },
  { id: 'stage-recipient', label: 'Confirming recipient address', duration: 1200 },
  { id: 'stage-amount', label: 'Verifying payment amount', duration: 1000 },
  { id: 'stage-confirms', label: 'Checking confirmation threshold', duration: 1400 },
];

export default function VerifyingStep() {
  const [completedStages, setCompletedStages] = useState<number>(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    let current = 0;
    const advance = () => {
      if (current < VERIFICATION_STAGES?.length) {
        const delay = VERIFICATION_STAGES?.[current]?.duration;
        setTimeout(() => {
          current++;
          setCompletedStages(current);
          advance();
        }, delay);
      }
    };
    advance();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedSeconds((s) => s + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col items-center gap-8 py-4 animate-fade-in">
      {/* Spinner */}
      <div className="relative w-20 h-20">
        <div className="absolute inset-0 rounded-full border-2 border-border" />
        <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-primary animate-spin" />
        <div className="absolute inset-2 rounded-full border border-transparent border-t-primary/30 animate-spin-slow" />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-primary font-black text-lg">₿</span>
        </div>
      </div>
      {/* Title */}
      <div className="text-center flex flex-col gap-2">
        <h3 className="text-lg font-bold text-foreground">Verifying on-chain</h3>
        <p className="text-sm text-muted-foreground">
          Querying the Bitcoin blockchain — this takes a few seconds
        </p>
        <p className="font-mono-data text-xs text-muted-foreground">{elapsedSeconds}s elapsed</p>
      </div>
      {/* Stages */}
      <div className="w-full flex flex-col gap-3">
        {VERIFICATION_STAGES?.map((stage, i) => {
          const isComplete = i < completedStages;
          const isActive = i === completedStages;
          return (
            <div key={stage?.id} className="flex items-center gap-3">
              <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center">
                {isComplete ? (
                  <div className="w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(34, 197, 94, 0.15)', border: '1px solid rgba(34, 197, 94, 0.4)' }}>
                    <svg className="w-3 h-3 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ) : isActive ? (
                  <div className="w-5 h-5 rounded-full border-2 border-transparent border-t-primary animate-spin" />
                ) : (
                  <div className="w-5 h-5 rounded-full"
                    style={{ background: 'var(--muted)', border: '1px solid var(--border)' }} />
                )}
              </div>
              <span className={`text-sm transition-colors duration-300 ${
                isComplete ? 'text-green-400' : isActive ? 'text-foreground verify-pulse' : 'text-muted-foreground'
              }`}>
                {stage?.label}
              </span>
            </div>
          );
        })}
      </div>
      {/* Warning */}
      <div className="w-full rounded-xl p-4 flex items-start gap-3"
        style={{ background: 'rgba(245, 158, 11, 0.06)', border: '1px solid rgba(245, 158, 11, 0.15)' }}>
        <svg className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Do not close this window. Verification is in progress. You will be redirected to Telegram automatically upon confirmation.
        </p>
      </div>
    </div>
  );
}